import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@src/components/ui/button/button';
import { useToggle } from '@src/components/hooks/useToggle';
import { ConfirmationDialog } from '@src/components/common/dialogs/confirmation-dialog';
import toastr from 'toastr';
import moment from 'moment';
import numeral from 'numeral';
import { cn } from '@src/utils/ui';

export const CancelTaskButton = ({
    buyerView,
    orderId,
    onCancelAndRefund,
    taskDate,
    total,
    className,
    fulfillmentStatus
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenCancel, toggleIsOpenCancel] = useToggle(false);
    const isMoreThan24hrs = (typeof taskDate === 'number' ? moment.unix(taskDate) : moment(taskDate)).isAfter(
        moment().add(24, 'hours')
    );
    fulfillmentStatus = buyerView ? fulfillmentStatus : '';

    const handleToggleOpen = e => {
        e.preventDefault();
        e.stopPropagation();
        toggleIsOpenCancel();
    };

    const handleCancel = async e => {
        e.preventDefault();
        e.stopPropagation();
        setIsLoading(true);

        try {
            await onCancelAndRefund(orderId);
            toastr.success('Order Cancelled');
        } catch (err) {
            console.log('Cancel and refund error:', err);
            const errorMessage = err?.message || 'Please try again later.';
            toastr.error(errorMessage, 'Oops! Something went wrong.');
        }

        setIsLoading(false);
        toggleIsOpenCancel(false);
    };

    const getModalCancelDescription = () => {
        if (!buyerView) return '';
        if (isMoreThan24hrs) {
            return 'Cancel at least 24 hours before the task date to avoid up to a $50 fee.';
        }
        return 'Your task starts in less than 24 hours. Cancelling now will result in up to a $50 cancellation fee.';
    };

    const getModalCancelBody = () => {
        if (!buyerView) return 'You are about to cancel this task. Are you sure?';
        return 'Are you sure you want to cancel this task?';
    };

    return (
        <div className={className}>
            <div
                className={cn([
                    'tw-flex tw-justify-between tw-items-center lg:tw-flex-row-reverse',
                    fulfillmentStatus ? 'tw-w-full' : ''
                ])}
            >
                <Button
                    onClick={handleToggleOpen}
                    variant='outlined'
                    className={cn(['tw-min-w-[100px]', fulfillmentStatus ? '' : 'tw-w-full lg:tw-w-[unset]'])}
                    loading={isOpenCancel && isLoading}
                >
                    Cancel
                </Button>
                {fulfillmentStatus}
            </div>

            <div
                className={cn([
                    'tw-text-[#BA1313] tw-text-[12px] tw-grow lg:tw-text-right tw-w-full',
                    isMoreThan24hrs || !buyerView ? 'tw-invisible tw-d-none lg:tw-block' : ''
                ])}
            >
                {isMoreThan24hrs || !buyerView
                    ? ''
                    : `Your task starts in less than 24 hours. Cancelling now will result in a ${numeral(
                          Math.min(total, 50)
                      ).format('$0,0.00')} cancellation fee.`}
            </div>
            <ConfirmationDialog
                open={isOpenCancel}
                onClose={handleToggleOpen}
                cancelLabel={buyerView ? 'GO BACK' : 'NO'}
                cancelProps={{ variant: 'outlined' }}
                onAgree={handleCancel}
                agreeLabel='YES, CANCEL'
                content={{
                    body: getModalCancelBody(),
                    description: getModalCancelDescription()
                }}
                loading={isLoading}
            />
        </div>
    );
};

CancelTaskButton.propTypes = {
    className: PropTypes.string,
    buyerView: PropTypes.bool,
    orderId: PropTypes.string.isRequired,
    onCancelAndRefund: PropTypes.func.isRequired,
    taskDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total: PropTypes.number,
    fulfillmentStatus: PropTypes.string
};
